import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  partyGameCardFromShell,
  registryEntriesToPartyGames,
} from "../party/modePartyCards";
import { apiClient } from "../utils/api";
import { useUser } from "./useAuth";
import { modesRegistryQueryKey } from "./useModesRegistry";

/** @deprecated Prefer `usePartyShellGames` — all point at modes registry for cache invalidation. */
export const partyGamesQueryKey = modesRegistryQueryKey;
export const partyPacksQueryKey = modesRegistryQueryKey;
export const partyMeLibraryQueryKey = modesRegistryQueryKey;

export function usePartyGameByShellId(shellId: string | undefined) {
  const { data: user } = useUser();
  const registryQ = useQuery({
    queryKey: modesRegistryQueryKey,
    queryFn: () => apiClient.getModesRegistry(),
    staleTime: 10 * 60 * 1000,
  });

  const data = useMemo(() => {
    if (!shellId || !registryQ.data) return undefined;
    return partyGameCardFromShell(shellId, registryQ.data, Boolean(user));
  }, [shellId, registryQ.data, user]);

  return {
    data,
    isLoading: registryQ.isLoading && !registryQ.data,
    isError: registryQ.isError,
    error: registryQ.error,
    refetch: registryQ.refetch,
  };
}

/** @deprecated No server packs — returns empty list. */
export function usePartyPacks() {
  return {
    data: [] as import("../types/api").PartyPackRow[],
    isLoading: false,
    isError: false,
    refetch: () => Promise.resolve(),
  };
}

/** @deprecated Use `usePartyShellGames` — uses registry + local chrome only. */
export function usePartyGames() {
  const { data: user } = useUser();
  const registryQ = useQuery({
    queryKey: modesRegistryQueryKey,
    queryFn: () => apiClient.getModesRegistry(),
    staleTime: 10 * 60 * 1000,
  });
  const authed = Boolean(user);
  const data = useMemo(
    () => registryEntriesToPartyGames(registryQ.data ?? [], authed),
    [registryQ.data, authed]
  );
  return {
    data,
    isLoading: registryQ.isLoading,
    isError: registryQ.isError,
    refetch: registryQ.refetch,
  };
}

/** @deprecated No `/api/catalog/party/me/library/` — use `usePartyShellGames` + profile credits. */
export function usePartyMeLibrary(_enabled?: boolean) {
  return usePartyGames();
}

/** @deprecated No party access endpoint — treat as free/owned from registry. */
export function usePartyMeAccess(_shellId: string | undefined, _enabled: boolean) {
  return {
    data: undefined,
    isLoading: false,
    isError: false,
    refetch: () => Promise.resolve(),
  };
}
